import React, { useRef, useEffect, useState, useLayoutEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

const SCROLL_STEP = 50;

const SCROLL_DEBOUNCE_MS = 16;

const LAYOUT_SETTLE_DELAY_MS = 400;

const SCROLLBAR_VISIBILITY_THRESHOLD = 20;
const STATE_CHANGE_TOLERANCE = 2;
const SCROLL_EDGE_TOLERANCE = 1;

const MIN_THUMB_HEIGHT = 30;

const THUMB_SIZE_RATIO = 0.5;

const ARIA_VALUE_MIN = 0;
const ARIA_VALUE_MAX = 100;

let instanceCounter = 0;

const ScrollableContainer = ({ children, className, resetScrollKey }) => {
    const scrollableIdRef = useRef(null);
    if (scrollableIdRef.current === null) {
        instanceCounter += 1;
        scrollableIdRef.current = `scrollable-content-${instanceCounter}`;
    }
    const scrollableId = scrollableIdRef.current;

    const scrollContainerRef = useRef(null);
    const contentRef = useRef(null);
    const timeoutRef = useRef(null);
    const prevResetKeyRef = useRef(resetScrollKey);
    const trackRef = useRef(null);
    const imageLoadHandlersRef = useRef([]);

    const [scrollState, setScrollState] = useState({
        scrollTop: 0,
        scrollHeight: 0,
        clientHeight: 0,
        showScrollbar: false,
        trackHeight: 0,
    });

    const [isDragging, setIsDragging] = useState(false);
    const dragStartY = useRef(0);
    const dragStartScrollTop = useRef(0);

    const updateScrollState = useCallback((forceUpdate = false) => {
        const container = scrollContainerRef.current;
        const track = trackRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const showScrollbar = scrollHeight > clientHeight + SCROLLBAR_VISIBILITY_THRESHOLD;
        const trackHeight = track ? track.clientHeight : 0;

        setScrollState(prev => {
            if (
                !forceUpdate &&
                Math.abs(prev.scrollTop - scrollTop) < STATE_CHANGE_TOLERANCE &&
                Math.abs(prev.scrollHeight - scrollHeight) < STATE_CHANGE_TOLERANCE &&
                Math.abs(prev.clientHeight - clientHeight) < STATE_CHANGE_TOLERANCE &&
                Math.abs(prev.trackHeight - trackHeight) < STATE_CHANGE_TOLERANCE &&
                prev.showScrollbar === showScrollbar
            )
                return prev;

            return { scrollTop, scrollHeight, clientHeight, showScrollbar, trackHeight };
        });
    }, []);

    const scheduleProgressiveUpdate = useCallback(() => {
        clearTimeout(timeoutRef.current);
        updateScrollState(true);
        timeoutRef.current = setTimeout(() => updateScrollState(true), LAYOUT_SETTLE_DELAY_MS);
    }, [updateScrollState]);

    const setContentRef = useCallback(
        node => {
            if (node === contentRef.current) return;

            clearTimeout(timeoutRef.current);
            imageLoadHandlersRef.current.forEach(({ img, handler }) => {
                img.removeEventListener('load', handler);
            });
            imageLoadHandlersRef.current = [];

            contentRef.current = node;

            if (node) {
                scheduleProgressiveUpdate();
                node.querySelectorAll('img').forEach(img => {
                    if (!img.complete) {
                        const handler = () => scheduleProgressiveUpdate();
                        img.addEventListener('load', handler, { once: true });
                        imageLoadHandlersRef.current.push({ img, handler });
                    }
                });
            }
        },
        [scheduleProgressiveUpdate],
    );

    useLayoutEffect(() => {
        if (resetScrollKey !== undefined && resetScrollKey !== prevResetKeyRef.current) {
            if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
            prevResetKeyRef.current = resetScrollKey;
        }
        if (trackRef.current) {
            scheduleProgressiveUpdate();
        }
    }, [scheduleProgressiveUpdate, resetScrollKey]);

    useEffect(
        () => () => {
            clearTimeout(timeoutRef.current);
            imageLoadHandlersRef.current.forEach(({ img, handler }) => {
                img.removeEventListener('load', handler);
            });
            imageLoadHandlersRef.current = [];
        },
        [],
    );

    // Event handlers and observers
    useEffect(() => {
        const container = scrollContainerRef.current;
        const content = contentRef.current;
        const track = trackRef.current;
        if (!container) return undefined;

        let scrollTimeout = null;
        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(updateScrollState, SCROLL_DEBOUNCE_MS);
        };

        const handleResize = () => scheduleProgressiveUpdate();

        container.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize, { passive: true });

        const containerObserver =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => scheduleProgressiveUpdate())
                : null;
        const contentObserver =
            content && typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => scheduleProgressiveUpdate())
                : null;
        const trackObserver =
            track && typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => updateScrollState())
                : null;

        // Watch for content DOM changes (children added/removed)
        const mutationObserver =
            content && typeof MutationObserver !== 'undefined'
                ? new MutationObserver(() => scheduleProgressiveUpdate())
                : null;

        if (containerObserver) containerObserver.observe(container);
        if (contentObserver) contentObserver.observe(content);
        if (trackObserver) trackObserver.observe(track);
        if (mutationObserver) {
            mutationObserver.observe(content, {
                childList: true,
                subtree: true,
            });
        }

        updateScrollState();

        return () => {
            clearTimeout(scrollTimeout);
            container.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            if (containerObserver) containerObserver.disconnect();
            if (contentObserver) contentObserver.disconnect();
            if (trackObserver) trackObserver.disconnect();
            if (mutationObserver) mutationObserver.disconnect();
        };
    }, [updateScrollState, scheduleProgressiveUpdate]);

    // Scroll controls
    const scroll = useCallback(direction => {
        if (scrollContainerRef.current)
            scrollContainerRef.current.scrollTop += direction * SCROLL_STEP;
    }, []);

    const scrollUp = useCallback(() => scroll(-1), [scroll]);
    const scrollDown = useCallback(() => scroll(1), [scroll]);

    // Unified drag start handler
    const startDrag = useCallback(clientY => {
        setIsDragging(true);
        dragStartY.current = clientY;
        dragStartScrollTop.current = scrollContainerRef.current
            ? scrollContainerRef.current.scrollTop
            : 0;
    }, []);

    // Thumb drag handlers
    const handleThumbMouseDown = useCallback(
        e => {
            e.preventDefault();
            startDrag(e.clientY);
        },
        [startDrag],
    );

    const handleThumbTouchStart = useCallback(
        e => {
            e.preventDefault();
            startDrag(e.touches[0].clientY);
        },
        [startDrag],
    );

    // Unified track jump handler
    const jumpToPosition = useCallback(clientY => {
        const container = scrollContainerRef.current;
        const track = trackRef.current;
        if (!container || !track) return;

        const { scrollHeight, clientHeight } = container;
        const trackRect = track.getBoundingClientRect();
        const positionY = clientY - trackRect.top;
        const trackHeight = track.clientHeight;

        // Calculate scroll position based on click/touch position
        const scrollPercentage = positionY / trackHeight;
        const maxScrollTop = scrollHeight - clientHeight;
        container.scrollTop = scrollPercentage * maxScrollTop;
    }, []);

    // Track click/tap handlers
    const handleTrackClick = useCallback(
        e => {
            if (e.target.classList.contains('scroll-thumb')) return;
            jumpToPosition(e.clientY);
        },
        [jumpToPosition],
    );

    const handleTrackTouch = useCallback(
        e => {
            if (e.target.classList.contains('scroll-thumb')) return;
            e.preventDefault();
            jumpToPosition(e.touches[0].clientY);
        },
        [jumpToPosition],
    );

    // Unified drag move handler
    const handleDragMove = useCallback(clientY => {
        const container = scrollContainerRef.current;
        const track = trackRef.current;
        if (!container || !track) return;

        const { scrollHeight, clientHeight } = container;
        const trackHeight = track.clientHeight;
        const deltaY = clientY - dragStartY.current;

        // Convert movement to scroll position
        const scrollRange = scrollHeight - clientHeight;
        const scrollDelta = (deltaY / trackHeight) * scrollRange;
        container.scrollTop = dragStartScrollTop.current + scrollDelta;
    }, []);

    useEffect(() => {
        if (!isDragging) return undefined;

        const handleMouseMove = e => handleDragMove(e.clientY);
        const handleTouchMove = e => handleDragMove(e.touches[0].clientY);
        const handleDragEnd = () => setIsDragging(false);

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleDragEnd);
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleDragEnd);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleDragEnd);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleDragEnd);
        };
    }, [isDragging, handleDragMove]);

    // Thumb styling with memoization
    const thumbStyle = useMemo(() => {
        const { scrollTop, scrollHeight, clientHeight, showScrollbar, trackHeight } = scrollState;
        if (!showScrollbar) return { display: 'none' };

        // Prevent invalid calculations before layout is ready
        if (scrollHeight <= 0 || clientHeight <= 0) {
            return { display: 'none' };
        }

        const effectiveTrackHeight = trackHeight > 0 ? trackHeight : clientHeight;

        // Additional guard: ensure effective track height is valid
        if (effectiveTrackHeight <= 0) {
            return { display: 'none' };
        }

        const height = Math.max(
            (clientHeight / scrollHeight) * effectiveTrackHeight * THUMB_SIZE_RATIO,
            MIN_THUMB_HEIGHT,
        );
        const scrollPercentage =
            scrollHeight > clientHeight ? scrollTop / (scrollHeight - clientHeight) : 0;
        const maxTop = effectiveTrackHeight - height;
        const top = scrollPercentage * maxTop;

        return {
            height: `${height}px`,
            top: `${Math.min(Math.max(ARIA_VALUE_MIN, top), maxTop)}px`,
            cursor: isDragging ? 'grabbing' : 'grab',
        };
    }, [scrollState, isDragging]);

    const { scrollTop, scrollHeight, clientHeight, showScrollbar } = scrollState;

    // Check if at top or bottom with small tolerance for sub-pixel rounding
    const isAtTop = scrollTop <= SCROLL_EDGE_TOLERANCE;
    const isAtBottom = scrollTop >= scrollHeight - clientHeight - SCROLL_EDGE_TOLERANCE;

    // Scroll percentage (0–100) exposed to assistive technologies via aria-valuenow
    const ariaValueNow = Math.round(
        (scrollTop / Math.max(scrollHeight - clientHeight, SCROLL_EDGE_TOLERANCE)) * ARIA_VALUE_MAX,
    );

    // Handle wheel events on the custom scrollbar
    const handleScrollbarWheel = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop += e.deltaY;
        }
    }, []);

    const handleButtonKeyDown = useCallback((e, action) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            action();
        }
    }, []);

    const handleSliderKeyDown = useCallback(e => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const {
            scrollHeight: containerScrollHeight,
            clientHeight: containerClientHeight,
        } = container;
        const maxScrollTop = containerScrollHeight - containerClientHeight;
        let handled = false;

        switch (e.key) {
            case 'ArrowUp':
                container.scrollTop = Math.max(0, container.scrollTop - SCROLL_STEP);
                handled = true;
                break;
            case 'ArrowDown':
                container.scrollTop = Math.min(maxScrollTop, container.scrollTop + SCROLL_STEP);
                handled = true;
                break;
            case 'PageUp':
                container.scrollTop = Math.max(0, container.scrollTop - containerClientHeight);
                handled = true;
                break;
            case 'PageDown':
                container.scrollTop = Math.min(
                    maxScrollTop,
                    container.scrollTop + containerClientHeight,
                );
                handled = true;
                break;
            case 'Home':
                container.scrollTop = 0;
                handled = true;
                break;
            case 'End':
                container.scrollTop = maxScrollTop;
                handled = true;
                break;
            default:
                break;
        }

        if (handled) {
            e.preventDefault();
        }
    }, []);

    return (
        <div className={`${className || ''} scrollable-content`.trim()}>
            <div
                id={scrollableId}
                className="content-wrapper"
                ref={scrollContainerRef}
                role="region"
                aria-label="Scrollable content area"
                tabIndex={0}
                style={{
                    overflowY: showScrollbar ? 'auto' : 'hidden',
                    overflowX: 'hidden',
                    height: '100%',
                }}
            >
                <div ref={setContentRef}>{children}</div>
            </div>
            {showScrollbar && (
                <div
                    className="custom-scroll-bar"
                    onWheel={handleScrollbarWheel}
                    role="group"
                    aria-label="Scroll controls"
                >
                    <div
                        className={`triangle-up${isAtTop ? ' disabled' : ''}`}
                        onClick={isAtTop ? undefined : scrollUp}
                        onKeyDown={isAtTop ? undefined : e => handleButtonKeyDown(e, scrollUp)}
                        role="button"
                        aria-label="Scroll up"
                        aria-disabled={isAtTop}
                        tabIndex={isAtTop ? -1 : 0}
                    />
                    <div
                        className="scroll-track"
                        ref={trackRef}
                        onClick={handleTrackClick}
                        onTouchStart={handleTrackTouch}
                        role="presentation"
                        aria-hidden="true"
                    >
                        <div
                            className="scroll-thumb"
                            style={thumbStyle}
                            onMouseDown={handleThumbMouseDown}
                            onTouchStart={handleThumbTouchStart}
                            onKeyDown={handleSliderKeyDown}
                            role="slider"
                            aria-label="Scroll position"
                            aria-controls={scrollableId}
                            aria-orientation="vertical"
                            aria-valuemin={ARIA_VALUE_MIN}
                            aria-valuemax={ARIA_VALUE_MAX}
                            aria-valuenow={ariaValueNow}
                            tabIndex={0}
                        />
                    </div>
                    <div
                        className={`triangle-down${isAtBottom ? ' disabled' : ''}`}
                        onClick={isAtBottom ? undefined : scrollDown}
                        onKeyDown={isAtBottom ? undefined : e => handleButtonKeyDown(e, scrollDown)}
                        role="button"
                        aria-label="Scroll down"
                        aria-disabled={isAtBottom}
                        tabIndex={isAtBottom ? -1 : 0}
                    />
                </div>
            )}
        </div>
    );
};

ScrollableContainer.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    resetScrollKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ScrollableContainer.defaultProps = {
    children: null,
    className: '',
    resetScrollKey: undefined,
};

export default ScrollableContainer;
